import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin, Tooltip} from "antd";
import React, {useEffect} from "react";
import {Trans, t} from "@lingui/macro";
import {useLingui} from "@lingui/react";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";
import {InfoCircleOutlined} from "@ant-design/icons";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const DdsAssignmentRuleDrawer = (props: Interface) => {

    const { i18n } = useLingui();
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();

        if(props.mode === 'Edit') {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'validFrom' || key === 'validTo') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else if(key === 'itemNo') {
                    fieldsValues.push({name: key, value: value});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
            }
            form.setFields(fieldsValues);
        }
    }, []);

    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .post('/resource/items/page', {
                    start: 0,
                    length: 1000,
                    direction: 'asc',
                    sorted_field: 'id',
                    query: {
                    }
                })
                .then(response => {
                    const items = Array.isArray(response.data.data) ? response.data.data : [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error);
                    openNotification('error', 'Failed to load items');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        } else {
            setItemOptions([]);
        }
    };

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            axiosInstance.current
                .put(
                    '/resource/dds-assignment-rules/' + encodeURIComponent(form.getFieldValue(['id'])) + '/from-dto',
                    {
                        id: form.getFieldValue(['id']),
                        item: {
                            itemNo: form.getFieldValue(['itemNo'])
                        },
                        ddsNumber: form.getFieldValue(['ddsNumber']),
                        quantity: form.getFieldValue(['quantity']),
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully updated.');
                    props.onClose && props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            axiosInstance.current
                .post(
                    '/resource/dds-assignment-rules/from-dto',
                    {
                        item: {
                            itemNo: form.getFieldValue(['itemNo'])
                        },
                        ddsNumber: form.getFieldValue(['ddsNumber']),
                        quantity: form.getFieldValue(['quantity']),
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null
                    },
                    config,
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully created.');
                    props.onClose && props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Form
            id="newDdsAssignmentRule"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24} style={{marginBottom: 20}}><Col span={12}><span style={{color: "red", fontStyle: "italic"}}>* Mandantory fields</span></Col></Row>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="itemNo"
                            label={<Trans>MSC Part Number</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.itemNo} value={item.itemNo}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ddsNumber"
                            label={
                                <span>
                                    <Trans>DDS Number</Trans>
                                    {' '}
                                    <Tooltip title={<Trans>The existing DDS number</Trans>}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[{ required: true, message: <Trans>Please enter the DDS number</Trans> }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="quantity"
                            label={
                                <span>
                                    <Trans>Quantity</Trans>
                                    {' '}
                                    <Tooltip title={<Trans>Number of MSC parts covered by this DDS.</Trans>}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </span>
                            }
                        >
                            <InputNumber min={1} controls={false} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="validFrom"
                            label={<Trans>DDS Valid From</Trans>}
                            dependencies={['validTo']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const validTo = getFieldValue('validTo');
                                        if (!value || !validTo || moment(validTo).diff(moment(value), 'years', true) <= 1) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(i18n._(t`The interval between DDS Valid From and DDS Valid To cannot exceed 1 year`)));
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                onChange={() => form.validateFields(['validTo'])}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="validTo"
                            label={
                                <span>
                                    <Trans>DDS Valid To</Trans>
                                </span>
                            }
                            dependencies={['validFrom']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const validFrom = getFieldValue('validFrom');
                                        if (!value || !validFrom || moment(value).diff(moment(validFrom), 'years', true) <= 1) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(i18n._(t`The interval between DDS Valid From and DDS Valid To cannot exceed 1 year`)));
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                onChange={() => form.validateFields(['validFrom'])}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div>
                <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default DdsAssignmentRuleDrawer;
